import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Spinner, Badge } from 'flowbite-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetTicketById, useAddTicketMessage, useUpdateTicketStatus } from 'src/hooks/supportTicket.hooks';
import { SupportTicketStatus, MessageSenderType } from 'types';
import { HiX, HiPaperAirplane } from 'react-icons/hi';
import Container from 'src/components/shared/Container';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getSupportQuillModules, attachSupportQuillPasteHandler } from 'src/components/support/supportQuillConfig';

const stripHtml = (html: string): string => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
};

const QUILL_MODULES = getSupportQuillModules();

const AMTicketView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [isEditorExpanded, setIsEditorExpanded] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const quillRef = useRef<any>(null);
    const pasteCleanupRef = useRef<(() => void) | null>(null);

    const { data: ticketData, isLoading } = useGetTicketById(id || '');
    const ticket = (ticketData as any)?.data;

    const addMessageMutation = useAddTicketMessage();
    const updateStatusMutation = useUpdateTicketStatus();

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [ticket?.messages]);

    // Attach paste handler so pasted images upload to S3 instead of Base64
    useEffect(() => {
        const timer = setTimeout(() => {
            const editor = quillRef.current?.getEditor?.();
            if (editor) {
                pasteCleanupRef.current?.();
                pasteCleanupRef.current = attachSupportQuillPasteHandler(editor);
            }
        }, 100);
        return () => {
            clearTimeout(timer);
            pasteCleanupRef.current?.();
            pasteCleanupRef.current = null;
        };
    }, []);

    const handleSendMessage = () => {
        if (!message.trim() || !id) return;

        // Send full HTML (including embedded images) so it is stored and shown
        addMessageMutation.mutate(
            {
                ticketId: id,
                data: { message },
            },
            {
                onSuccess: () => {
                    setMessage('');
                },
            },
        );
    };

    const editorHeight = isEditorExpanded ? 380 : 220;

    const handleUpdateStatus = (status: SupportTicketStatus) => {
        if (!id) return;

        updateStatusMutation.mutate({
            id,
            data: { status },
        });
    };

    const getStatusBadge = (status: SupportTicketStatus | string) => {
        const colors: Record<SupportTicketStatus, string> = {
            [SupportTicketStatus.OPEN]: 'info',
            [SupportTicketStatus.RESOLVED]: 'success',
        };
        return colors[status as SupportTicketStatus] ?? (status === 'in_progress' || status === 'escalated' ? 'info' : status === 'closed' ? 'success' : 'gray');
    };

    const getStatusLabel = (status: string) =>
        status === 'in_progress' || status === 'escalated' ? 'Open' : status === 'closed' ? 'Resolved' : (status?.charAt(0)?.toUpperCase() ?? '') + (status?.slice(1) ?? '');

    if (isLoading) {
        return (
            <Container className="w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%] font-inter">
                <div className="flex justify-center py-8">
                    <Spinner size="xl" />
                </div>
            </Container>
        );
    }

    if (!ticket) {
        return (
            <Container className="w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%] font-inter">
                <div className="text-center py-8 text-gray-500">Ticket not found</div>
            </Container>
        );
    }

    const isClosed = ticket.status === SupportTicketStatus.RESOLVED || ticket.status === 'closed';

    return (
        <Container className="w-[92%] max-w-[1280px] pt-2 md:pb-2 pb-10 sm:w-[96%] font-inter">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold dark:text-white">{ticket.subject}</h1>
                        <div className="flex gap-2 mt-2">
                            <Badge color={getStatusBadge(ticket.status)}>{getStatusLabel(ticket.status)}</Badge>
                            {(ticket._id || id) && (
                                <span className="text-sm text-gray-500 dark:text-gray-400">ID: {ticket._id || id}</span>
                            )}
                        </div>
                    </div>
                    <Button color="gray" size="sm" onClick={() => navigate('/tickets')}>
                        <HiX className="w-5 h-5 mr-2" />
                        Close
                    </Button>
                </div>
            </div>

            <Card className="bg-white dark:bg-[#1F2A37] mb-4">
                {/* Ticket Info */}
                <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Student</label>
                        <p className="text-gray-900 dark:text-white">
                            {ticket.user?.name} ({ticket.user?.email})
                        </p>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Category</label>
                        <p className="text-gray-900 dark:text-white">
                            {typeof ticket.category === 'object' && ticket.category !== null
                                ? (ticket.category?.label || ticket.category?.name || '')
                                : (ticket.category ?? '')}
                        </p>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Created</label>
                        <p className="text-gray-900 dark:text-white">
                            {new Date(ticket.createdAt).toLocaleString()}
                        </p>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Escalation Level</label>
                        <p className="text-gray-900 dark:text-white">{ticket.currentEscalationLevel}</p>
                    </div>
                </div>

                {/* Status Actions */}
                {/* Status Actions - only Mark as Resolved (Close Ticket removed) */}
                <div className="flex gap-2 mb-4">
                    {!isClosed && (
                        <>
                            <Button
                                size="sm"
                                color="success"
                                onClick={() => handleUpdateStatus(SupportTicketStatus.RESOLVED)}
                                disabled={updateStatusMutation.isLoading}
                            >
                                Mark as Resolved
                            </Button>
                        </>
                    )}
                    {isClosed && (
                        <Button
                            size="sm"
                            color="purplePlaced"
                            onClick={() => handleUpdateStatus(SupportTicketStatus.OPEN)}
                            disabled={updateStatusMutation.isLoading}
                        >
                            Reopen Ticket
                        </Button>
                    )}
                </div>
            </Card>

            <Card className="bg-white dark:bg-[#1F2A37]">
                {/* Messages: first = student subject (left); then SupportTicketMessage: user left, agent/system right */}
                <div className="border rounded-lg p-4 h-[500px] overflow-y-auto mb-4 bg-gray-50 dark:bg-gray-800">
                    {(() => {
                        const messagesList = ticket.messages || [];
                        const parseTime = (v: any) => {
                            const t = v ? new Date(v).getTime() : NaN;
                            return Number.isFinite(t) ? t : 0;
                        };
                        const earliestSystemMsg = [...messagesList]
                            .filter((m: any) => m?.senderType === MessageSenderType.SYSTEM && (m?.message || '').trim())
                            .sort((a: any, b: any) => parseTime(a.createdAt) - parseTime(b.createdAt))[0];
                        const systemId = earliestSystemMsg?._id;

                        const isWelcomeMessage = (m: any) =>
                            m?.senderType === MessageSenderType.AGENT &&
                            typeof m?.message === 'string' &&
                            m.message.includes('Student Experience Team') &&
                            m.message.trim().startsWith('Dear student');
                        const earliestWelcomeMsg = [...messagesList]
                            .filter((m: any) => isWelcomeMessage(m))
                            .sort((a: any, b: any) => parseTime(a.createdAt) - parseTime(b.createdAt))[0];
                        const welcomeId = earliestWelcomeMsg?._id;

                        const remainingMessages = messagesList.filter((m: any) => m?._id !== systemId && m?._id !== welcomeId);

                        return (
                            <>
                                {/* 1) Answer first (system) */}
                                {earliestSystemMsg && (
                                    <div className="mb-4 flex justify-end">
                                        <div className="max-w-[70%] rounded-lg p-3 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                            <div className="text-xs mb-1 opacity-75">
                                                System • {new Date(earliestSystemMsg.createdAt).toLocaleString()}
                                            </div>
                                            <div className="support-ticket-content prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap">{earliestSystemMsg.message || ''}</div>
                                        </div>
                                    </div>
                                )}

                                {/* 2) Student typed message */}
                                {ticket.subject && (
                                    <div className="mb-4 flex justify-start">
                                        <div className="max-w-[70%] rounded-lg p-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white">
                                            <div className="text-xs mb-1 opacity-75">
                                                {ticket.user?.name || 'Student'} • {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : ''}
                                            </div>
                                            <div className="support-ticket-content prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: ticket.subject }} />
                                        </div>
                                    </div>
                                )}

                                {/* 3) Auto welcome */}
                                {earliestWelcomeMsg && (
                                    <div className="mb-4 flex justify-end">
                                        <div className="max-w-[70%] rounded-lg p-3 bg-purple-600 text-white">
                                            <div className="text-xs mb-1 opacity-75">
                                                {earliestWelcomeMsg.sender?.name || 'Support'} • {new Date(earliestWelcomeMsg.createdAt).toLocaleString()}
                                            </div>
											<div className="support-ticket-content prose prose-sm prose-invert max-w-none whitespace-pre-wrap text-white">{earliestWelcomeMsg.message || ''}</div>
                                        </div>
                                    </div>
                                )}

                                {/* 4) Remaining conversation */}
                                {remainingMessages.map((msg: any) => {
                                    const isAgentOrSystem = msg.senderType === MessageSenderType.AGENT || msg.senderType === MessageSenderType.SYSTEM;
                                    return (
                                        <div
                                            key={msg._id}
                                            className={`mb-4 flex ${isAgentOrSystem ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div
                                                className={`max-w-[70%] rounded-lg p-3 ${msg.senderType === MessageSenderType.AGENT
                                                    ? 'bg-purple-600 text-white'
                                                    : msg.senderType === MessageSenderType.SYSTEM
                                                        ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                                                    }`}
                                            >
                                                <div className="text-xs mb-1 opacity-75">
                                                    {msg.sender?.name || 'System'} • {new Date(msg.createdAt).toLocaleString()}
                                                </div>
												<div
													className={`support-ticket-content prose prose-sm max-w-none whitespace-pre-wrap ${msg.senderType === MessageSenderType.AGENT
														? 'prose-invert text-white'
														: 'dark:prose-invert'
													}`}
												>
													{msg.message || ''}
												</div>
                                                {msg.attachments?.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {msg.attachments.map((url: string) => (
                                                            <a key={url} href={url} target="_blank" rel="noopener noreferrer" className="block">
                                                                <img src={url} alt="Attachment" className="max-h-48 rounded object-contain border border-gray-200 dark:border-gray-600" />
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </>
                        );
                    })()}
                    <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                {!isClosed && (
                    <div className="mt-4">
                        <div
                            className="relative flex flex-col rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700 [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-200 [&_.ql-toolbar]:pr-20 dark:[&_.ql-toolbar]:border-gray-600"
                            style={{ height: editorHeight + 80 }}
                        >
                            <div className="absolute top-2 right-2 z-10 flex gap-1">
                                <button
                                    type="button"
                                    onClick={() => setIsEditorExpanded(false)}
                                    className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white"
                                    aria-label="Minimize"
                                    title="Minimize"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsEditorExpanded(true)}
                                    className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white"
                                    aria-label="Maximize"
                                    title="Maximize"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                                    </svg>
                                </button>
                            </div>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={message}
                                onChange={setMessage}
                                placeholder="Type your reply..."
                                modules={QUILL_MODULES}
                                className="!flex !flex-col !h-full [&_.ql-toolbar]:flex-shrink-0 [&_.ql-container]:flex-1 [&_.ql-container]:min-h-0 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[180px]"
                            />
                        </div>
                        <div className="flex justify-end mt-2">
                            <Button
                                color="purplePlaced"
                                onClick={handleSendMessage}
                                disabled={!message.trim() || addMessageMutation.isLoading}
                            >
                                {addMessageMutation.isLoading ? (
                                    <Spinner size="sm" className="mr-2" />
                                ) : (
                                    <HiPaperAirplane className="w-5 h-5 mr-2" />
                                )}
                                Send
                            </Button>
                        </div>
                    </div>
                )}

                {isClosed && (
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-2">
                        This ticket has been resolved. You can reopen it using the button above.
                    </div>
                )}
            </Card>
        </Container>
    );
};

export default AMTicketView;

